import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import api from "@/lib/api";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 20 } });
      setItems(data.items);
      setUnread(data.unread);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const openPanel = () => { setOpen((v) => !v); if (!open) load(); };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); setItems((p) => p.map((n) => ({ ...n, read: true }))); setUnread(0); } catch { /* ignore */ }
  };

  const clickItem = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
      setItems((p) => p.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    }
    setOpen(false);
    if (n.url) {
      try {
        const u = new URL(n.url);
        navigate(u.pathname + u.search);
      } catch { navigate(n.url); }
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell"
        onClick={openPanel}
        className="relative text-[#1E3F33] hover:text-[#CC5A3A] p-2 rounded-full hover:bg-[#1E3F33]/5 transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" strokeWidth={1.7} />
        {unread > 0 && (
          <span data-testid="notification-unread-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#CC5A3A] text-white text-[10px] font-semibold grid place-items-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-panel" className="absolute right-0 mt-2 w-80 max-w-[90vw] rounded-2xl border border-[#1E3F33]/10 bg-white shadow-xl overflow-hidden z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#1E3F33]/8">
            <span className="font-heading text-sm text-[#1E3F33]">Notifications</span>
            {unread > 0 && (
              <button data-testid="notification-mark-all" onClick={markAll} className="text-xs text-[#2D6A4F] hover:underline inline-flex items-center gap-1">
                <CheckCheck className="w-3.5 h-3.5" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-auto">
            {items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-[#8A9A86]">No notifications yet.</p>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  data-testid={`notification-item-${n.id}`}
                  onClick={() => clickItem(n)}
                  className={`w-full text-left px-4 py-3 border-b border-[#1E3F33]/5 hover:bg-[#F0EFEA] transition-colors ${n.read ? "" : "bg-[#CC5A3A]/5"}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-[#CC5A3A] shrink-0" />}
                    <div className={n.read ? "pl-4" : ""}>
                      <div className="text-sm font-medium text-[#1E3F33]">{n.title}</div>
                      <div className="text-xs text-[#5C6B64] mt-0.5">{n.body}</div>
                      <div className="text-[10px] text-[#8A9A86] mt-1">{timeAgo(n.created_at)}</div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
